/**
 * lib/bankRecon.ts
 * ------------------------------------------------------------------
 * FINANCE — bank-statement reconciliation (pure).
 *
 * Parses a bank statement CSV, then matches each money-in (credit)
 * line to an open invoice — first by invoice number found in the
 * line's reference/description, then by student name as a fallback.
 * Lines already posted (idempotency) are detected via a stable
 * per-line id derived from the line's own fields, recorded on the
 * resulting payment (Payment.statementLineId).
 *
 * No parsing libraries — a small RFC-4180-ish CSV reader is included.
 * Framework-free; the screen drives the workflow.
 * ------------------------------------------------------------------ */
import type { Invoice } from '@/lib/financeCore';

/* ----------------------------- CSV ----------------------------- */

export interface ParsedCsv {
  headers: string[];
  rows: string[][];
}

/** Minimal CSV parser: handles quoted fields, embedded commas/quotes/newlines. */
export function parseCsv(text: string): ParsedCsv {
  const clean = text.replace(/^\uFEFF/, ''); // strip BOM
  const rows: string[][] = [];
  let field = '';
  let row: string[] = [];
  let inQuotes = false;
  for (let i = 0; i < clean.length; i++) {
    const c = clean[i];
    if (inQuotes) {
      if (c === '"') {
        if (clean[i + 1] === '"') { field += '"'; i++; } else { inQuotes = false; }
      } else field += c;
    } else if (c === '"') {
      inQuotes = true;
    } else if (c === ',') {
      row.push(field); field = '';
    } else if (c === '\n' || c === '\r') {
      if (c === '\r' && clean[i + 1] === '\n') i++;
      row.push(field); field = '';
      if (row.some((v) => v.trim() !== '')) rows.push(row);
      row = [];
    } else field += c;
  }
  if (field !== '' || row.length > 0) { row.push(field); if (row.some((v) => v.trim() !== '')) rows.push(row); }
  const headers = rows.length ? rows[0].map((h) => h.trim()) : [];
  return { headers, rows: rows.slice(1) };
}

/* --------------------------- LINES ----------------------------- */

export interface ColumnMap {
  date: number;
  description: number;
  amount: number;
  reference: number; // -1 if none
  /** true if the amount column is money-in only; false if signed (negative = debit). */
  amountIsCreditOnly: boolean;
}

export interface StatementLine {
  id: string;
  date: string;
  description: string;
  reference: string;
  amount: number; // positive credit (money in)
  raw: string[];
}

function parseAmount(s: string): number {
  const n = Number(String(s).replace(/[^0-9.\-]/g, ''));
  return Number.isFinite(n) ? n : 0;
}

/** FNV-1a hash → hex, for a stable per-line id. */
function hashId(input: string): string {
  let h = 0x811c9dc5;
  for (let i = 0; i < input.length; i++) {
    h ^= input.charCodeAt(i);
    h = Math.imul(h, 0x01000193);
  }
  return (h >>> 0).toString(16).padStart(8, '0');
}

/** Build credit-only statement lines from parsed rows + a column map. */
export function buildStatementLines(rows: string[][], map: ColumnMap): StatementLine[] {
  const out: StatementLine[] = [];
  for (const r of rows) {
    const rawAmt = parseAmount(r[map.amount] ?? '');
    const amount = map.amountIsCreditOnly ? Math.abs(rawAmt) : rawAmt;
    if (amount <= 0) continue; // money-in only
    const date = (r[map.date] ?? '').trim();
    const description = (r[map.description] ?? '').trim();
    const reference = map.reference >= 0 ? (r[map.reference] ?? '').trim() : '';
    const id = `L${hashId(`${date}|${amount.toFixed(2)}|${reference}|${description}`)}`;
    out.push({ id, date, description, reference, amount, raw: r });
  }
  return out;
}

/* --------------------------- MATCH ----------------------------- */

export type MatchStatus = 'posted' | 'matched' | 'ambiguous' | 'unmatched';

export interface MatchResult {
  line: StatementLine;
  status: MatchStatus;
  /** Chosen invoice id when matched. */
  invoiceId?: string;
  /** Candidate invoice ids when ambiguous. */
  candidates: string[];
  /** How the match was made, for the reviewer. */
  reason: 'already-posted' | 'invoice-number' | 'student-name' | 'none' | 'multiple';
}

function norm(s: string): string {
  return s.toUpperCase().replace(/[^A-Z0-9]/g, '');
}
function normName(s: string): string {
  return s.toLowerCase().replace(/[^a-z0-9 ]/g, ' ').replace(/\s+/g, ' ').trim();
}

export function isOpen(inv: Invoice): boolean {
  return inv.status !== 'void' && inv.balance > 0;
}

/**
 * Classify each line against the open invoices. `postedLineIds` are the
 * statementLineIds already on existing payments (idempotency).
 */
export function matchStatement(
  lines: StatementLine[],
  invoices: Invoice[],
  postedLineIds: Set<string>,
): MatchResult[] {
  const open = invoices.filter(isOpen);
  const byInvoiceNo = open.map((inv) => ({ inv, key: norm(inv.invoiceNo) })).filter((x) => x.key.length >= 4);
  const openByStudent = new Map<string, Invoice[]>();
  for (const inv of open) {
    const arr = openByStudent.get(inv.studentId) ?? openByStudent.set(inv.studentId, []).get(inv.studentId)!;
    arr.push(inv);
  }

  return lines.map((line) => {
    if (postedLineIds.has(line.id)) {
      return { line, status: 'posted', candidates: [], reason: 'already-posted' };
    }
    const hay = norm(`${line.reference} ${line.description}`);
    const nameHay = normName(`${line.reference} ${line.description}`);

    // 1) Invoice number in the line text.
    const numHits = byInvoiceNo.filter((x) => hay.includes(x.key));
    if (numHits.length === 1) return { line, status: 'matched', invoiceId: numHits[0].inv.id, candidates: [numHits[0].inv.id], reason: 'invoice-number' };
    if (numHits.length > 1) return { line, status: 'ambiguous', candidates: numHits.map((x) => x.inv.id), reason: 'multiple' };

    // 2) Student name fallback.
    const studentHits: Invoice[] = [];
    for (const inv of open) {
      const name = normName(inv.studentName ?? '');
      const tokens = name.split(' ').filter((t) => t.length >= 2);
      if (tokens.length >= 2 && tokens.every((t) => nameHay.includes(t))) studentHits.push(inv);
    }
    const distinctStudents = new Set(studentHits.map((i) => i.studentId));
    if (distinctStudents.size === 1) {
      const sid = [...distinctStudents][0];
      const invs = openByStudent.get(sid) ?? [];
      if (invs.length === 1) return { line, status: 'matched', invoiceId: invs[0].id, candidates: invs.map((i) => i.id), reason: 'student-name' };
      if (invs.length > 1) return { line, status: 'ambiguous', candidates: invs.map((i) => i.id), reason: 'multiple' };
    }
    if (distinctStudents.size > 1) {
      return { line, status: 'ambiguous', candidates: studentHits.map((i) => i.id), reason: 'multiple' };
    }

    return { line, status: 'unmatched', candidates: [], reason: 'none' };
  });
}

/** Guess a sensible default column mapping from header names. */
export function guessColumnMap(headers: string[]): ColumnMap {
  const idx = (re: RegExp, fallback: number) => {
    const i = headers.findIndex((h) => re.test(h.toLowerCase()));
    return i >= 0 ? i : fallback;
  };
  const credit = headers.findIndex((h) => /credit|money in|deposit|cr\b/i.test(h));
  const amount = credit >= 0 ? credit : idx(/amount|value/, -1);
  return {
    date: idx(/date|posted|transaction date/, 0),
    description: idx(/desc|narrative|details|particular/, 1),
    amount: amount >= 0 ? amount : Math.min(2, headers.length - 1),
    reference: idx(/ref|payment ref|cheque|transaction id/, -1),
    amountIsCreditOnly: credit >= 0,
  };
}
